<template>
  <div class="salon-app salon-time">
    <!-- Top app bar + booking progress. This screen names itself in the bar rather than
         repeating "Salon Booking" over a headline, the way the reference draws it. -->
    <header class="salon-topbar">
      <div class="salon-topbar-row">
        <button class="salon-back" type="button" :aria-label="$t('salon.common.goBack')" @click="$emit('back')">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <h1 class="salon-topbar-title">{{ $t("salon.time.title") }}</h1>
        <SalonProfileMenu
          :initials="memberInitials"
          :name="memberName"
          @navigate="$emit('profile', $event)"
        />
      </div>
      <div class="salon-progress">
        <div class="salon-progress-track">
          <div class="salon-progress-fill" :style="{ width: progressWidth }"></div>
        </div>
        <span class="salon-progress-label">{{ $t("salon.time.stepOf", { step, total: totalSteps }) }}</span>
      </div>
    </header>

    <main class="salon-main">
      <!-- Which day this grid belongs to, so the customer never has to go back to check -->
      <div class="salon-datepill-row">
        <span class="salon-datepill">
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <rect x="3" y="5" width="18" height="16" rx="2" fill="none" stroke="currentColor"
              stroke-width="1.6" />
            <path d="M3 10h18M8 3v4M16 3v4" fill="none" stroke="currentColor" stroke-width="1.6"
              stroke-linecap="round" />
          </svg>
          <span>{{ dateLabel }}</span>
        </span>

        <!-- SH026 — staff-first: whose grid this is, next to the day it belongs to. -->
        <span v-if="staffName" class="salon-datepill salon-datepill--staff">
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <circle cx="12" cy="8" r="3.5" fill="none" stroke="currentColor" stroke-width="1.6" />
            <path d="M5 20c1.2-3.4 4-5 7-5s5.8 1.6 7 5" fill="none" stroke="currentColor"
              stroke-width="1.6" stroke-linecap="round" />
          </svg>
          <span>{{ staffName }}</span>
        </span>
      </div>

      <p v-if="!isLoading && (!isOpen || isStaffOff || isServiceOff)" class="salon-closed">
        {{ hoursText || $t("salon.time.closedFallback") }}
      </p>

      <p v-else-if="!isLoading && loadFailed" class="salon-closed salon-closed--error">
        {{ $t("salon.time.loadError") }}
        <button class="salon-retry" type="button" @click="getSlots">{{ $t("salon.time.retry") }}</button>
      </p>

      <template v-else>
        <section v-for="section in visibleSections" :key="section.key" class="salon-section">
          <div class="salon-section-head">
            <h2 class="salon-section-title">{{ sectionLabelOf(section) }}</h2>
            <span class="salon-section-rule"></span>
          </div>

          <div class="salon-slots">
            <button
              v-for="slot in section.slots"
              :key="section.key + '-' + slot.time"
              class="salon-slot"
              :class="{
                'salon-slot--selected': slot.time === selectedTime,
                'salon-slot--off': !slot.is_available,
              }"
              type="button"
              :disabled="!slot.is_available"
              :aria-pressed="String(slot.time === selectedTime)"
              @click="selectedTime = slot.time"
            >
              {{ slot.label }}
            </button>
          </div>
        </section>

        <p v-if="!isLoading && !hasAnySlot" class="salon-closed">
          {{ $t("salon.time.noTimes") }}
        </p>

        <div v-if="hasAnySlot" class="salon-legend">
          <span class="salon-legend-item">
            <span class="salon-legend-swatch salon-legend-swatch--selected"></span>{{ $t("salon.time.selected") }}
          </span>
          <span class="salon-legend-item">
            <span class="salon-legend-swatch"></span>{{ $t("salon.time.available") }}
          </span>
          <span class="salon-legend-item">
            <span class="salon-legend-struck">00:00</span>{{ $t("salon.time.booked") }}
          </span>
        </div>
      </template>
    </main>

    <nav class="salon-cta">
      <p class="salon-cta-summary">
        <span>{{ dateLabel }}</span>
        <span class="salon-cta-dot" aria-hidden="true"></span>
        <span class="salon-cta-time">{{ selectedLabel || $t("salon.time.selectTime") }}</span>
        <template v-if="durationMin">
          <span class="salon-cta-dot" aria-hidden="true"></span>
          <span>{{ $t("salon.time.minutes", { n: durationMin }) }}</span>
        </template>
      </p>
      <button class="salon-cta-btn" type="button" :disabled="!selectedTime" @click="goToNextStep">
        <span>{{ nextLabelText }}</span>
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path d="M5 12h14m-6-6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </nav>

    <material-loading :active="isLoading" />
  </div>
</template>

<script>
import MaterialLoading from "@/components/MaterialLoading.vue";
import SalonBookingService from "@/services/salonBooking.service";
import SalonProfileMenu from "@/salon/components/ProfileMenu.vue";
import "@/salon/styles/tokens.css";

// SH007 is the seventh of the nine salon booking steps (Salon_UI/select_time) — SH026's
// channel setting can move it, which is what the `step` prop is for.
const STEP = 7;
const TOTAL_STEPS = 9;

export default {
  name: "SalonSelectTime",
  components: {
    SalonProfileMenu,
    MaterialLoading,
  },
  props: {
    // SH016 — the avatar in the top bar, and who it stands for.
    memberInitials: {
      type: String,
      default: "",
    },
    memberName: {
      type: String,
      default: "",
    },
    branchCode: {
      type: String,
      default: "",
    },
    date: {
      type: String,
      default: "",
    },
    // "Tue, 15 Sep 2026" from SH006. The endpoint returns its own shorter label, which
    // takes over once the response lands; this keeps the pill filled until then.
    dateText: {
      type: String,
      default: "",
    },
    durationMin: {
      type: Number,
      default: 0,
    },
    // SH037 — the services chosen at SH004/SH005. The grid only offers the hours they are
    // actually sold in, so a colour on a 9 AM - noon schedule cannot be booked for 8 PM.
    services: {
      type: Array,
      default: () => [],
    },
    // Set when the customer is coming back from SH008. This view is destroyed on the way
    // out, so without it Back would drop them back to "Select a time" having already
    // chosen one — the same reset SH006 needed initialDate for.
    initialTime: {
      type: String,
      default: "",
    },
    // SH026 — the stylist chosen before the date on a staff-first channel. The grid then
    // shows the times they can take rather than the channel's.
    staffId: {
      type: [Number, String],
      default: "",
    },
    staffName: {
      type: String,
      default: "",
    },
    step: {
      type: Number,
      default: STEP,
    },
    // The time is the last question on a staff-first channel, and the middle one on a
    // date-first channel.
    nextLabel: {
      type: String,
      default: "Continue",
    },
  },
  emits: ["back", "selected", "profile"],
  data() {
    return {
      totalSteps: TOTAL_STEPS,
      isLoading: false,
      loadFailed: false,
      isOpen: true,
      // SH026 — the outlet is trading but the chosen stylist is not in that day. Kept
      // apart from isOpen so the line explains which of the two it is.
      isStaffOff: false,
      // SH037 — the outlet is open and the stylist is in, but the chosen service is not
      // sold on this date. The API's own line says which of the three it is; without this
      // the screen would render three empty headings and no explanation.
      isServiceOff: false,
      hoursText: "",
      apiDateText: "",
      sections: [],
      selectedTime: this.initialTime,
    };
  },
  computed: {
    progressWidth() {
      return ((this.step / TOTAL_STEPS) * 100).toFixed(2) + "%";
    },

    // 0 is "No Preference", which is the whole channel's grid rather than one stylist's.
    staffFilter() {
      return Number(this.staffId) > 0 ? Number(this.staffId) : undefined;
    },

    // SH037 — the item ids behind those services; left out entirely when nothing has been
    // chosen, which is what tells the API to restrict nothing.
    serviceIds() {
      const ids = this.services
        .map((service) => Number(service.item_id || service.id || 0))
        .filter((id) => id > 0);

      return ids.length ? ids : undefined;
    },

    dateLabel() {
      // SH046 — "9月15日 周二" in Chinese, rebuilt from the date itself so it follows a
      // language switch; English keeps the API's "Tue, 15 Sep" untouched.
      if (this.$i18n.locale === "zh") {
        const [year, month, day] = String(this.date || "").split("-").map(Number);

        if (year && month && day) {
          const moment = new Date(Date.UTC(year, month - 1, day));
          const format = (options) =>
            new Intl.DateTimeFormat("zh-CN", { timeZone: "UTC", ...options }).format(moment);

          return format({ month: "long", day: "numeric" }) + " " + format({ weekday: "short" });
        }
      }

      return this.apiDateText || this.dateText;
    },

    // SH046 — BookingFlow passes an already translated label, which is shown as given; only
    // the prop's English default is looked up, so the screen still follows the language
    // when it is mounted without one.
    nextLabelText() {
      const keys = {
        Continue: "salon.time.continue",
        "Review booking": "salon.time.reviewBooking",
      };

      return keys[this.nextLabel] ? this.$t(keys[this.nextLabel]) : this.nextLabel;
    },

    // A section with nothing in it is dropped rather than left as a bare heading over
    // empty space — an outlet that shuts at lunchtime has no evening to show.
    visibleSections() {
      return this.sections.filter((section) => (section.slots || []).length);
    },

    hasAnySlot() {
      return this.visibleSections.length > 0;
    },

    allSlots() {
      return this.sections.flatMap((section) => section.slots || []);
    },

    selectedSlot() {
      return this.allSlots.find((slot) => slot.time === this.selectedTime) || null;
    },

    selectedLabel() {
      return this.selectedSlot ? this.selectedSlot.label : "";
    },
  },
  created() {
    this.getSlots();
  },
  methods: {
    async getSlots() {
      if (!this.branchCode || !this.date) {
        return;
      }

      this.isLoading = true;
      this.loadFailed = false;

      try {
        const response = await SalonBookingService.getTimeSlots({
          branch_code: this.branchCode,
          date: this.date,
          duration_min: this.durationMin || undefined,
          staff_id: this.staffFilter,
          service_ids: this.serviceIds,
        });

        if (response.data.result.isSuccess) {
          const data = response.data.data;

          this.isOpen = !!data.is_open;
          this.isStaffOff = !!data.is_staff_off;
          // Only when the outlet is open and the stylist is in: a closed day and a day
          // off already have their own line, and claiming the service is unavailable on
          // top of either would be the wrong reason.
          this.isServiceOff =
            !!data.is_open && !data.is_staff_off && !this.hasSlots(data.sections);
          this.hoursText = data.hours_text || "";
          this.apiDateText = data.date_text || "";
          this.sections = data.sections ?? [];
          this.applyDefaultSelection();
        } else {
          this.sections = [];
          this.loadFailed = true;
        }
      } catch (error) {
        // A closed outlet and a broken endpoint look identical once the grid is empty,
        // and only one of them is worth offering a Retry for.
        this.sections = [];
        this.loadFailed = true;
      } finally {
        this.isLoading = false;
      }
    },

    // SH046 — the API names the three sections in English; translate the ones we know by
    // key and leave anything new as the API wrote it.
    sectionLabelOf(section) {
      const keys = {
        morning: "salon.time.morning",
        afternoon: "salon.time.afternoon",
        evening: "salon.time.evening",
      };

      return keys[section.key] ? this.$t(keys[section.key]) : section.label;
    },

    /** Whether a response carries any half hour at all, in any section. */
    hasSlots(sections) {
      return (sections ?? []).some((section) => (section.slots ?? []).length > 0);
    },

    // Nothing is pre-selected: a time is the one choice on this screen, and filling it in
    // for the customer risks them tapping Continue on a slot they never read.
    applyDefaultSelection() {
      const stillThere = this.allSlots.find(
        (slot) => slot.time === this.selectedTime && slot.is_available
      );

      if (!stillThere) {
        this.selectedTime = "";
      }
    },

    goToNextStep() {
      if (!this.selectedSlot) {
        return;
      }

      // SH008 (pick a stylist) comes next; it needs the whole appointment window, not
      // just the start, to know which staff are free.
      this.$emit("selected", {
        date: this.date,
        date_text: this.dateLabel,
        time: this.selectedSlot.time,
        time_text: this.selectedSlot.label,
        is_next_day: !!this.selectedSlot.is_next_day,
        duration_min: this.durationMin,
      });
    },
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-time {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  max-width: var(--salon-max-width);
  margin: 0 auto;
  background: var(--salon-background);
  color: var(--salon-on-background);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
}

/* ── Top app bar ───────────────────────────────────────────────── */
.salon-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  padding: 16px var(--salon-container-padding) 8px;
  background: var(--salon-surface);
  border-bottom: 2px solid var(--salon-surface-variant);
}

.salon-topbar-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
}

.salon-back {
  position: absolute;
  left: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  color: var(--salon-on-surface);
  background: transparent;
  border: 0;
  cursor: pointer;
}

.salon-back:active {
  transform: scale(0.95);
}

.salon-topbar-title {
  margin: 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: var(--salon-on-surface);
}

.salon-progress {
  margin-top: 8px;
  text-align: center;
}

.salon-progress-track {
  width: 100%;
  height: 3px;
  overflow: hidden;
  background: var(--salon-surface-variant);
  border-radius: 999px;
}

.salon-progress-fill {
  height: 100%;
  background: var(--salon-primary);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.salon-progress-label {
  display: inline-block;
  margin-top: 8px;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

/* ── Canvas ────────────────────────────────────────────────────── */
.salon-main {
  flex: 1;
  padding: var(--salon-stack-lg) var(--salon-container-padding) 160px;
}

.salon-datepill-row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--salon-stack-sm);
  justify-content: flex-end;
  margin-bottom: var(--salon-stack-lg);
}

.salon-datepill {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--salon-on-surface);
  background: var(--salon-surface-container);
  border: 1px solid var(--salon-border);
  border-radius: 999px;
  box-shadow: var(--salon-shadow-soft);
}

/* SH026 — the stylist pill sits beside the date one, quieter than it: the day is what
   this screen is about, the stylist is context carried in from the step before. */
.salon-datepill--staff {
  color: var(--salon-on-surface-variant);
  background: var(--salon-surface-container-low);
}

/* ── Time sections ─────────────────────────────────────────────── */
.salon-section {
  margin-bottom: 32px;
}

.salon-section-head {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;
}

.salon-section-title {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--salon-on-surface-variant);
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.salon-section-rule {
  flex: 1;
  height: 1px;
  background: var(--salon-border);
}

.salon-slots {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.salon-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface);
  background: var(--salon-surface);
  border: 1px solid var(--salon-surface-container-highest);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;
}

.salon-slot:hover:not(:disabled) {
  border-color: var(--salon-primary);
}

.salon-slot--selected {
  font-weight: 500;
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border-color: var(--salon-primary);
  box-shadow: 0 4px 10px rgba(28, 28, 30, 0.18);
}

/* Struck through rather than hidden: that is what tells the customer the salon is busy
   at this hour rather than shut for the day. */
.salon-slot--off {
  color: var(--salon-on-surface-variant);
  background: var(--salon-surface);
  border-color: var(--salon-border);
  cursor: not-allowed;
  opacity: 0.6;
  text-decoration: line-through;
}

/* ── Legend ────────────────────────────────────────────────────── */
.salon-legend {
  display: flex;
  gap: 24px;
  align-items: center;
  justify-content: center;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid var(--salon-border);
}

.salon-legend-item {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

.salon-legend-swatch {
  width: 12px;
  height: 12px;
  background: var(--salon-surface);
  border: 1px solid var(--salon-surface-container-highest);
  border-radius: 999px;
}

.salon-legend-swatch--selected {
  background: var(--salon-primary);
  border-color: var(--salon-primary);
}

.salon-legend-struck {
  color: var(--salon-on-surface-variant);
  opacity: 0.6;
  text-decoration: line-through;
}

/* ── Closed / empty ────────────────────────────────────────────── */
.salon-closed {
  padding: 32px 0;
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
  text-align: center;
}

.salon-closed--error {
  color: var(--salon-error);
}

.salon-retry {
  display: block;
  padding: 8px 20px;
  margin: 12px auto 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  color: var(--salon-primary);
  background: transparent;
  border: 1px solid var(--salon-surface-container-highest);
  border-radius: 999px;
  cursor: pointer;
}

/* ── Bottom CTA ────────────────────────────────────────────────── */
.salon-cta {
  position: fixed;
  bottom: 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: var(--salon-max-width);
  padding: 16px var(--salon-container-padding)
    calc(16px + env(safe-area-inset-bottom, 0px));
  background: var(--salon-surface);
  border-top: 1px solid var(--salon-surface-variant);
  box-shadow: 0 -4px 20px rgba(28, 28, 30, 0.04);
}

.salon-cta-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: center;
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

.salon-cta-time {
  font-weight: 500;
  color: var(--salon-on-surface);
}

.salon-cta-dot {
  width: 4px;
  height: 4px;
  background: var(--salon-surface-container-highest);
  border-radius: 999px;
}

.salon-cta-btn {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(28, 28, 30, 0.18);
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.salon-cta-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.salon-cta-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.salon-cta-btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}
</style>
